import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Pyramid, Database, Loader2, ArrowRight, ArrowLeft, Search, CheckCircle2, Sigma } from 'lucide-react';

// Import Pyramid PQL calculations straight into a model's calculated measures.
export function PyramidFormulaImportDialog({ open, onOpenChange, modelId, modelName, onImported }) {
  const [step, setStep] = useState('connection'); // connection | pick | done
  const [busy, setBusy] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState('');
  const [calcs, setCalcs] = useState([]);
  const [filter, setFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [result, setResult] = useState(null);
  const [latestOnly, setLatestOnly] = useState(true);

  const reset = useCallback(() => {
    setStep('connection'); setBusy(false); setConnections([]); setConnectionId('');
    setCalcs([]); setFilter(''); setSelectedIds([]); setResult(null); setLatestOnly(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    setBusy(true);
    api.listPyramidConnections()
      .then((rows) => setConnections(rows || []))
      .catch((e) => toast.error(e.message || 'Could not load connections'))
      .finally(() => setBusy(false));
  }, [open, reset]);

  const loadCalcs = async (latest = latestOnly) => {
    if (!connectionId) { toast.error('Select a connection'); return; }
    setBusy(true);
    try {
      const res = await api.listPyramidCalculations({ source_id: connectionId, model_id: modelId, latest_only: latest });
      setCalcs(res.calculations || []);
      setStep('pick');
      if (!(res.calculations || []).length) toast.message('No calculations found in this repository.');
    } catch (e) { toast.error(e.message || 'Could not read calculations'); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const filtered = calcs.filter((c) => !filter || (c.name || '').toLowerCase().includes(filter.toLowerCase()));

  const runImport = async () => {
    if (!selectedIds.length) { toast.error('Select at least one calculation'); return; }
    setBusy(true);
    try {
      const res = await api.importPyramidCalculations({ source_id: connectionId, model_id: modelId, calculation_ids: selectedIds });
      setResult(res);
      setStep('done');
      const c = res.measures_created || 0;
      const f = res.measures_flagged || 0;
      if ((c || f) && onImported) onImported();
      if (c || f) toast.success(`${c} imported${f ? `, ${f} need fixing` : ''}`);
      else toast.message('Nothing new imported — already present in this model.');
    } catch (e) { toast.error(e.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg overflow-hidden" data-testid="pyramid-formula-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pyramid className="w-5 h-5 text-amber-400" /> Import formulas from Pyramid</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground -mt-1">
          Pull Pyramid calculations into <strong>{modelName || 'this model'}</strong> as calculated measures.
        </DialogDescription>

        {step === 'connection' && (
          <div className="space-y-4 mt-2" data-testid="pyramid-formula-step-connection">
            <div>
              <Label className="text-sm">Pyramid content repository (PostgreSQL)</Label>
              {busy && !connections.length ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : connections.length ? (
                <Select value={connectionId} onValueChange={setConnectionId}>
                  <SelectTrigger className="mt-1" data-testid="pyramid-formula-connection-select"><SelectValue placeholder="Select a connection…" /></SelectTrigger>
                  <SelectContent>{connections.map((c) => <SelectItem key={c.source_id} value={c.source_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="mt-2 p-3 rounded-lg bg-muted/20 text-xs text-muted-foreground flex items-start gap-2">
                  <Database className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                  <span>Add a PostgreSQL data source pointing at your Pyramid repository in <strong>Data Sources</strong> first.</span>
                </div>
              )}
            </div>
            <Button onClick={() => loadCalcs()} disabled={busy || !connectionId} className="w-full gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-formula-load-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Browse calculations
            </Button>
          </div>
        )}

        {step === 'pick' && (
          <div className="space-y-3 mt-2" data-testid="pyramid-formula-step-pick">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search calculations…" className="pl-8" data-testid="pyramid-formula-search" />
              </div>
              <button onClick={() => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map((c) => c.id))}
                className="text-xs text-primary hover:underline whitespace-nowrap" data-testid="pyramid-formula-select-all">
                {selectedIds.length === filtered.length && filtered.length ? 'Clear' : 'Select all'}
              </button>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer" data-testid="pyramid-formula-latest-toggle">
              <Checkbox checked={latestOnly} onCheckedChange={(v) => { const nv = !!v; setLatestOnly(nv); loadCalcs(nv); }} />
              Latest versions only (hide older revisions)
            </label>
            <div className="max-h-72 overflow-auto space-y-1.5 pr-1">
              {filtered.map((c) => (
                <label key={c.id}
                  className={`w-full flex items-start gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${selectedIds.includes(c.id) ? 'border-primary/50 bg-primary/5' : 'border-border/40 bg-muted/10 hover:border-primary/30'}`}
                  data-testid={`pyramid-calc-${c.id}`}>
                  <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => toggle(c.id)} className="mt-0.5" />
                  <Sigma className="w-4 h-4 text-primary/70 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-sm font-medium truncate flex items-center gap-2">{c.name}<Badge variant="outline" className="text-[8px]">{c.type_label}</Badge>{(c.version !== null && c.version !== undefined) && <Badge variant="outline" className="text-[8px] border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300" data-testid={`pyramid-calc-version-${c.id}`}>v{c.version}</Badge>}</div>
                    {c.preview && (
                      <div className="mt-0.5 flex items-start gap-1.5 min-w-0" data-testid={`pyramid-calc-preview-${c.id}`}>
                        <Badge variant="outline" className={`text-[8px] shrink-0 mt-0.5 font-medium ${c.preview.status === 'manual' ? 'border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300' : c.preview.status === 'reused' ? 'border-sky-500/40 bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}>{c.preview.status === 'translated' ? 'converts' : c.preview.status}</Badge>
                        <div className="min-w-0 flex-1">
                          {c.preview.formula
                            ? <code className="block text-[10px] text-emerald-600 dark:text-emerald-300 break-all line-clamp-2">{c.preview.formula}</code>
                            : <span className="block text-[10px] text-muted-foreground line-clamp-2 break-words">{c.preview.note}</span>}
                        </div>
                      </div>
                    )}
                    {c.pql && <code className="block text-[10px] text-muted-foreground/80 break-all line-clamp-2">{c.pql}</code>}
                  </div>
                </label>
              ))}
              {!filtered.length && <div className="text-xs text-muted-foreground text-center py-8">No calculations match.</div>}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setStep('connection')} className="gap-2 text-xs"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
              <Button onClick={runImport} disabled={busy || !selectedIds.length} className="flex-1 gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-formula-import-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Import {selectedIds.length || ''}
              </Button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-3 mt-2" data-testid="pyramid-formula-step-done">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {result.measures_created} translated{(result.measures_flagged || 0) > 0 ? `, ${result.measures_flagged} imported with raw PQL to fix` : ''} — of {result.requested} selected.
            </div>
            <div className="max-h-64 overflow-auto space-y-1 pr-1">
              {result.formulas.map((f, i) => (
                <div key={i} className="text-xs px-2 py-1.5 rounded bg-muted/10 flex items-start gap-2" data-testid={`pyramid-formula-result-${f.status}`}>
                  <Badge variant="outline" className={`text-[8px] shrink-0 ${f.status === 'manual' ? 'border-amber-400/40 text-amber-400' : 'border-emerald-400/40 text-emerald-400'}`}>{f.status}</Badge>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="font-medium truncate">{f.name}</div>
                    <div className="text-[10px] text-muted-foreground break-words">{f.note}</div>
                    {f.pql && <code className="block text-[10px] text-muted-foreground/80 break-all line-clamp-2">{f.pql}</code>}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={() => onOpenChange(false)} className="w-full" variant="outline" data-testid="pyramid-formula-done-btn">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
